import React from "react";
import { MenuItem, TextField } from "@material-ui/core";
import {
	ToolbarIconBtn,
	ToolbarPrimaryBtn,
	ToolbarSecondaryBtn,
	ToolbarSection,
	ToolbarSpacer,
	ToolbarWrap,
} from "../styles";
import { LABELS } from "../labels";
import ScopeSwitcher from "./ScopeSwitcher";
import type { FilterState, TextRegistryScope } from "../types";

interface Props {
	langs: string[];
	scope: TextRegistryScope;
	filters: FilterState;
	screens: string[];
	groups: string[];
	tags: string[];
	draftDirty: boolean;
	saving: boolean;
	publishing: boolean;
	detectionPanelOpen: boolean;
	glossaryPanelOpen: boolean;
	newDetectionCount: number;
	onScopeChange: (scope: TextRegistryScope) => void;
	onFilterChange: (patch: Partial<FilterState>) => void;
	onFindReplace: () => void;
	onRefresh: () => void;
	onSaveDraft: () => void;
	onPublish: () => void;
	onToggleDetectionPanel: () => void;
	onToggleGlossaryPanel: () => void;
}

export default function Toolbar({
	langs,
	scope,
	filters,
	screens,
	groups,
	tags,
	draftDirty,
	saving,
	publishing,
	detectionPanelOpen,
	glossaryPanelOpen,
	newDetectionCount,
	onScopeChange,
	onFilterChange,
	onFindReplace,
	onRefresh,
	onSaveDraft,
	onPublish,
	onToggleDetectionPanel,
	onToggleGlossaryPanel,
}: Props) {
	return (
		<ToolbarWrap>
			<ToolbarSection>
				<ScopeSwitcher
					scope={scope}
					onChange={onScopeChange}
				/>
			</ToolbarSection>

			<ToolbarSection>
				<input
					type="text"
					placeholder={LABELS.toolbarSearch}
					value={filters.search}
					onChange={(e) => onFilterChange({ search: e.target.value })}
					style={{
						padding: "7px 10px",
						border: "1px solid #e2e8f0",
						borderRadius: 8,
						fontSize: "0.8125rem",
						width: 200,
						outline: "none",
						fontFamily: "inherit",
					}}
				/>

				{screens.length > 0 && (
					<TextField
						select
						size="small"
						variant="outlined"
						label={LABELS.filterScreen}
						value={filters.screen}
						onChange={(e) => onFilterChange({ screen: e.target.value })}
						style={{ minWidth: 120 }}>
						<MenuItem value="">{LABELS.filterAll}</MenuItem>
						{screens.map((s) => (
							<MenuItem
								key={s}
								value={s}>
								{s}
							</MenuItem>
						))}
					</TextField>
				)}

				{groups.length > 0 && (
					<TextField
						select
						size="small"
						variant="outlined"
						label={LABELS.filterGroup}
						value={filters.group}
						onChange={(e) => onFilterChange({ group: e.target.value })}
						style={{ minWidth: 120 }}>
						<MenuItem value="">{LABELS.filterAll}</MenuItem>
						{groups.map((g) => (
							<MenuItem
								key={g}
								value={g}>
								{g}
							</MenuItem>
						))}
					</TextField>
				)}

				{tags.length > 0 && (
					<TextField
						select
						size="small"
						variant="outlined"
						label={LABELS.filterTag}
						value={filters.tag}
						onChange={(e) => onFilterChange({ tag: e.target.value })}
						style={{ minWidth: 110 }}>
						<MenuItem value="">{LABELS.filterAll}</MenuItem>
						{tags.map((t) => (
							<MenuItem
								key={t}
								value={t}>
								{t}
							</MenuItem>
						))}
					</TextField>
				)}

				<TextField
					select
					size="small"
					variant="outlined"
					label={LABELS.filterStatus}
					value={filters.status}
					onChange={(e) => onFilterChange({ status: e.target.value as FilterState["status"] })}
					style={{ minWidth: 110 }}>
					<MenuItem value="">{LABELS.filterAll}</MenuItem>
					<MenuItem value="draft">Draft</MenuItem>
					<MenuItem value="published">Published</MenuItem>
				</TextField>

				{langs.length > 1 && (
					<TextField
						select
						size="small"
						variant="outlined"
						label={LABELS.filterLang}
						value={filters.translationLang}
						onChange={(e) => onFilterChange({ translationLang: e.target.value })}
						style={{ minWidth: 120 }}>
						{langs.map((lang) => (
							<MenuItem
								key={lang}
								value={lang}>
								{LABELS.langName(lang)}
							</MenuItem>
						))}
					</TextField>
				)}
				<TextField
					select
					size="small"
					variant="outlined"
					label={LABELS.langName(filters.translationLang || langs[0] || "vi")}
					value={filters.translationState}
					onChange={(e) =>
						onFilterChange({ translationState: e.target.value as FilterState["translationState"] })
					}
					style={{ minWidth: 120 }}>
					<MenuItem value="">{LABELS.filterAll}</MenuItem>
					<MenuItem value="missing">{LABELS.filterViMissing}</MenuItem>
					<MenuItem value="present">{LABELS.filterViPresent}</MenuItem>
				</TextField>
			</ToolbarSection>

			<ToolbarSpacer />

			<ToolbarSection>
				<ToolbarIconBtn
					onClick={onFindReplace}
					title={LABELS.toolbarFindReplace}>
					<i className="fa fa-search" />
					{LABELS.toolbarFindReplace}
				</ToolbarIconBtn>

				<ToolbarIconBtn
					active={detectionPanelOpen}
					onClick={onToggleDetectionPanel}
					title={LABELS.detectionInboxTitle}>
					<i className="fa fa-inbox" />
					{newDetectionCount > 0 && (
						<span
							style={{
								background: "#ef4444",
								color: "#fff",
								borderRadius: 8,
								fontSize: "0.6875rem",
								padding: "0 5px",
								fontWeight: 700,
							}}>
							{newDetectionCount}
						</span>
					)}
				</ToolbarIconBtn>

				<ToolbarIconBtn
					active={glossaryPanelOpen}
					onClick={onToggleGlossaryPanel}
					title={LABELS.toolbarGlossary}>
					<i className="fa fa-book" />
					{LABELS.toolbarGlossary}
				</ToolbarIconBtn>

				<ToolbarIconBtn
					onClick={onRefresh}
					title={LABELS.toolbarRefresh}>
					<i className="fa fa-refresh" />
				</ToolbarIconBtn>

				<ToolbarSecondaryBtn
					onClick={onSaveDraft}
					disabled={saving || !draftDirty}>
					{saving ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-floppy-o" />}
					{LABELS.toolbarSaveDraft}
				</ToolbarSecondaryBtn>

				<ToolbarPrimaryBtn
					onClick={onPublish}
					disabled={publishing}>
					{publishing ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-cloud-upload" />}
					{LABELS.toolbarPublish}
				</ToolbarPrimaryBtn>
			</ToolbarSection>
		</ToolbarWrap>
	);
}
